import type { LC, PropsWithChildren } from '@use-gpu/live';
import type { ViewUniforms } from '@use-gpu/core';
import type { Renderable } from './types';
import { OITOptions } from '../pass/types';

import { makeBoundingBox, extendBoundingBox, transformBounds, toDataBounds, resolve } from '@use-gpu/core';
import { use, yeet, memo, gather, useOne } from '@use-gpu/live';

import { useDeviceContext } from '../providers/device-provider';
import { useRenderContext } from '../providers/render-provider';
import { usePassContext } from '../providers/pass-provider';
import { QueueReconciler } from '../reconcilers/index';

import { useInspectable } from '../hooks/useInspectable'

import { useDynamicViewBinding, useApplyPassBindGroup } from '../pass/bindings';
import { getRenderPassDescriptor, drawToPass } from './util';

import { OITDispatch } from '../render/dispatch/oit-dispatch';
import { vec2 } from 'gl-matrix';

const {quote} = QueueReconciler;

type OITCommand = (
  commandEncoder: GPUCommandEncoder,
) => void;

export type OITPassProps = {
  env: {
    light: any,
  },
  calls: {
    oit?: Renderable[],
    debug?: Renderable[],
  },
  oit: OITOptions,
};

const label = 'OITPass';
const NO_OPS: any[] = [];
const toArray = <T>(x?: T[]): T[] => Array.isArray(x) ? x : NO_OPS;

/** OIT render pass.
*/
export const OITPass: LC<OITPassProps> = memo((props: PropsWithChildren<OITPassProps>) => {
  const {
    env,
    calls,
    oit: oitOptions,
  } = props;

  const inspect = useInspectable();

  const device = useDeviceContext();
  const renderContext = useRenderContext();

  const {
    targets: {oit},
    bindGroups: {color: viewBindGroup},
    views: {view: {uniforms: viewUniforms}},
  } = usePassContext();

  const [transparencyContext] = oit;

  // Bind to dynamic view
  const {bindGroup, cull, uniforms, upload: uploadView} = useDynamicViewBinding(viewBindGroup);
  const {bindPass, dataBindings} = useApplyPassBindGroup(env, bindGroup, label);

  const oits   = toArray(calls['oit']   as Renderable[]);
  const debugs = toArray(calls['debug'] as Renderable[]);

  const transparencyPassDescriptor = useOne(() =>
    getRenderPassDescriptor(transparencyContext, {label: 'OITPass/Transparency', merge: true}),
    transparencyContext);

  const resolvePassDescriptor = useOne(() =>
    getRenderPassDescriptor(renderContext, {label: 'OITPass/Resolve', overlay: true, merge: true}),
    renderContext);

  const debugPassDescriptor = useOne(() =>
    getRenderPassDescriptor(renderContext, {label: 'OITPass/Debug', overlay: true, merge: true}),
    renderContext);

  const resolveOIT = useOne(() => [
    use(OITDispatch, {
      ...oitOptions,
      mode: 'resolve',
      targetContext: renderContext,
      descriptor: resolvePassDescriptor,
      bindPass,
    }),
  ], [oit, oitOptions, renderContext]);

  const inspected = inspect({
    output: {
      sources: transparencyContext.sources,
    },
    pass: {
      uniforms,
      bindings: dataBindings,
    },
    render: {
      vertices: 0,
      triangles: 0,
    },
  });

  return gather(resolveOIT, (calls: {oit: OITCommand}[]) => {

    const nf = vec2.create();

    return quote(yeet(() => {
      let vs = 0;
      let ts = 0;

      const countGeometry = (v: number, t: number) => { vs += v; ts += t; };
      const commandEncoder = device.createCommandEncoder();

      // Get world space bounds
      const box = makeBoundingBox(3);
      for (const call of oits) {
        const {bounds} = call;
        if (bounds) {
          const {min, max} = resolve(bounds);
          extendBoundingBox(box, min, 3);
          extendBoundingBox(box, max, 3);
        }
        else {
          console.warn("OIT cannot be applied to an object without position bounds.");
        }
      }

      // Get view space bounds
      const bounds = toDataBounds(box);
      const {viewMatrix} = uniforms;
      transformBounds(viewMatrix.current)(bounds, bounds);

      // Adjust near/far plane to tight bounds (for OIT calc only)
      for (const k in viewUniforms) {
        uniforms[k as keyof ViewUniforms].current = viewUniforms[k].current;
      }

      const [near] = viewUniforms.viewNearFar.current;
      nf[0] = Math.max(near, -bounds.max[2]);
      nf[1] = Math.max(near, -bounds.min[2]);
      uniforms.viewNearFar.current = nf;

      uploadView(uniforms);

      // Draw transparency into MRT
      {
        const passEncoder = commandEncoder.beginRenderPass(transparencyPassDescriptor);
        bindPass?.(passEncoder);

        drawToPass(cull, oits, passEncoder, countGeometry, uniforms);

        passEncoder.end();
      }

      // Resolve OIT
      calls.forEach(({oit: f}) => f(commandEncoder));

      // Draw wireframes
      if (debugs.length) {
        const passEncoder = commandEncoder.beginRenderPass(debugPassDescriptor);
        bindPass?.(passEncoder);

        drawToPass(cull, debugs, passEncoder, countGeometry, uniforms);

        passEncoder.end();
      }

      const command = commandEncoder.finish();
      device.queue.submit([command]);

      inspected.render.vertices = vs;
      inspected.render.triangles = ts;

      return null;
    }));
  });
}, 'OITPass');


